import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import http from "../../services/http";
import { 
  FiGrid, 
  FiDollarSign, 
  FiTrendingUp, 
  FiTrendingDown,
  FiBarChart2,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiSearch,
  FiCalendar,
  FiAward,
  FiLayers,
  FiInbox,
  FiTarget,
  FiUsers
} from "react-icons/fi";
import { 
  BsJoystick,
  BsCashStack,
  BsCurrencyRupee,
  BsEmojiFrown,
  BsTrophy,
  BsArrowRepeat
} from "react-icons/bs";

export const SectionCReportLayerList = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalGamesFound, setTotalGamesFound] = useState(0);
  const [loading, setLoading] = useState(false);

  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);

  // Filter States
  const [leagueFilter, setLeagueFilter] = useState("");
  const [matchFilter, setMatchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  
  // Date Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [viewUsers, setViewUsers] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load initial static data
  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const [leagueRes, matchRes] = await Promise.all([
          getLeagues(),
          getMatches(),
        ]);
        if (leagueRes?.status) setLeagues(leagueRes.data || []);
        if (matchRes?.status) setMatches(matchRes.data || []);
      } catch (err) {
        console.error("Error loading static data", err);
        toast.error("Failed to load leagues/matches");
      }
    };
    loadStaticData();
  }, []);

  // Fetch report data whenever dates change
  useEffect(() => {
    loadReport();
  }, [fromDate, toDate]);

  const loadReport = async () => {
    setLoading(true);
    try {
      let url = "selection-game-report";
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      
      const queryString = params.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      const reportRes = await http.get(finalUrl);

      if (reportRes?.data?.status) {
        setData(reportRes.data.report || []);
        setSummary(reportRes.data.league_summary || null);
        setTotalGamesFound(reportRes.data.total_games_found || 0);
      }
    } catch (error) {
      toast.error("Failed to load report");
      console.error("Error loading report:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= MATCH DROPDOWN FILTER ================= */
  const filteredMatches = useMemo(() => {
    if (!leagueFilter) return matches;
    return matches.filter((m) => String(m.league_id) === String(leagueFilter));
  }, [matches, leagueFilter]);

  /* ================= MAIN FILTER ================= */
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const g = item.game_info;
      if (!g) return false;

      const relatedMatch = matches.find(
        (m) => String(m.id) === String(g.match_id),
      );

      const matchLeague =
        leagueFilter ?
          relatedMatch &&
          relatedMatch.league_id &&
          String(relatedMatch.league_id) === String(leagueFilter)
        : true;

      const matchMatch =
        matchFilter ? String(g.match_id) === String(matchFilter) : true;

      const matchStatus =
        statusFilter ?
          g.status?.toLowerCase() === statusFilter.toLowerCase()
        : true;

      const matchSearch =
        search ? g.title?.toLowerCase().includes(search.toLowerCase()) : true;

      return matchLeague && matchMatch && matchStatus && matchSearch;
    });
  }, [data, leagueFilter, matchFilter, statusFilter, search, matches]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [leagueFilter, matchFilter, statusFilter, search, pageSize, fromDate, toDate]);

  const getMatchName = (id) => {
    if (!id) return "N/A";
    const match = matches.find((m) => String(m.id) === String(id));
    return match ?
        `${match.team_a || "Team A"} vs ${match.team_b || "Team B"}`
      : `Match ID: ${id}`;
  };

  const getLeagueName = (leagueId) => {
    if (!leagueId) return "No League";
    const league = leagues.find((l) => String(l.id) === String(leagueId));
    return league ? league.league_name : `League ID: ${leagueId}`;
  };

  const handleViewUsers = (gameTitle, users) => {
    setViewUsers({ gameTitle, users });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setViewUsers(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    return status?.toLowerCase() === "live" ? "bg-success badge-live rounded-pill" : "bg-secondary rounded-pill";
  };

  return (
    <div className="container-fluid px-3 px-lg-4 mt-4 mb-5">
      <div className="card shadow-premium border-0 animate-fade-in-up">
        {/* ===== HEADER ===== */}
        <div className="card-header bg-white py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-center border-bottom-soft">
          <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
            <div className="bg-primary-soft p-3 rounded-4 shadow-sm">
              <FiLayers size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="mb-0 fw-bold text-dark">Section C Selection Report</h4>
              <p className="text-muted small mb-0">Outcome-based selection performance analytics</p>
            </div>
          </div>
          {totalGamesFound > 0 && (
            <div className="d-flex align-items-center gap-3">
               <span className="badge bg-primary-soft text-primary px-3 py-2 rounded-pill fw-bold">
                 {totalGamesFound} Active Game Nodes
               </span>
               <button className="btn btn-primary-gradient px-4 rounded-pill shadow-sm" onClick={loadReport} disabled={loading}>
                  <BsArrowRepeat className={`me-2 ${loading ? 'spin' : ''}`} />
                  Refresh Matrix
               </button>
            </div>
          )}
        </div>

        <div className="card-body p-4">
          {/* ===== SUMMARY TILES ===== */}
          {summary && (
            <div className="row g-4 mb-5">
              <div className="col-xl-2 col-md-4 col-sm-6 delay-1">
                <div className="summary-card grad-blue h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Total Games</p>
                  <h3 className="metric-value mb-0 fw-bold">{summary.total_games || 0}</h3>
                  <div className="mt-3 extra-small opacity-75"><BsJoystick className="me-1"/> Selection Series</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-2">
                <div className="summary-card grad-red h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Total Collection</p>
                  <h3 className="metric-value mb-0 fw-bold">{formatCurrency(summary.total_collection || 0).replace('₹','₹ ')}</h3>
                  <div className="mt-3 extra-small opacity-75"><BsCurrencyRupee className="me-1"/> Incoming Revenue</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-3">
                <div className="summary-card grad-green h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Admin Earning</p>
                  <h3 className="metric-value mb-0 fw-bold">{formatCurrency(summary.total_admin_earning || 0).replace('₹','₹ ')}</h3>
                  <div className="mt-3 extra-small opacity-75"><FiTrendingUp className="me-1"/> Net Optimization</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-4">
                <div className="summary-card grad-indigo h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Distributed</p>
                  <h3 className="metric-value mb-0 fw-bold">{formatCurrency(summary.total_distributed || 0).replace('₹','₹ ')}</h3>
                  <div className="mt-3 extra-small opacity-75"><BsCashStack className="me-1"/> User Outflow</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-5">
                <div className="summary-card grad-gold h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Winner Count</p>
                  <h3 className="metric-value mb-0 fw-bold">{summary.total_winners || 0}</h3>
                  <div className="mt-3 extra-small opacity-75"><BsTrophy className="me-1"/> Successful Nodes</div>
                </div>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6 delay-6">
                <div className="summary-card grad-purple h-100 shadow-lg">
                  <p className="metric-label opacity-75 small fw-bold mb-2">Loser Count</p>
                  <h3 className="metric-value mb-0 fw-bold">{summary.total_losers || 0}</h3>
                  <div className="mt-3 extra-small opacity-75"><BsEmojiFrown className="me-1"/> Deductions</div>
                </div>
              </div>
            </div>
          )}

          {/* ===== PREMIUM FILTERS ===== */}
          <div className="premium-filter-bar p-4 rounded-4 mb-4 shadow-sm border animate-fade-in-up">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiCalendar className="me-2"/>From Date</label>
                <input
                  type="date"
                  className="form-control border-0 bg-light-soft rounded-3"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiCalendar className="me-2"/>To Date</label>
                <input
                  type="date"
                  className="form-control border-0 bg-light-soft rounded-3"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiAward className="me-2"/>League Analytics</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={leagueFilter}
                  onChange={(e) => {
                    setLeagueFilter(e.target.value);
                    setMatchFilter("");
                  }}>
                  <option value="">All Available Leagues</option>
                  {leagues.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.league_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label text-muted small fw-bold"><FiTarget className="me-2"/>Match Selection</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={matchFilter}
                  onChange={(e) => setMatchFilter(e.target.value)}
                  disabled={!leagueFilter && filteredMatches.length === 0}>
                  <option value="">All Contextual Matches</option>
                  {filteredMatches.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.team_a || "Team A"} vs {m.team_b || "Team B"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label text-muted small fw-bold"><FiSearch className="me-2"/>Search Title</label>
                <input
                  type="text"
                  className="form-control border-0 bg-light-soft rounded-3"
                  placeholder="Scan Game Titles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label text-muted small fw-bold"><FiFilter className="me-2"/>Status Scope</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">Check All Status</option>
                  <option value="live">Live Now</option>
                  <option value="completed">Fully Completed</option>
                </select>
              </div>

              <div className="col-md-4 d-flex align-items-end">
                 <button className="btn btn-primary w-100 rounded-pill py-2 shadow-sm fw-bold" onClick={loadReport}>
                    <FiSearch className="me-2"/> Execute Matrix Search
                 </button>
              </div>
            </div>
          </div>

          {/* ===== LOADING STATE ===== */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary shadow-sm" role="status" style={{width: '3rem', height: '3rem'}}>
                <span className="visually-hidden">Scanning...</span>
              </div>
              <p className="mt-3 fw-medium text-primary">Interrogating Selection Matrix...</p>
            </div>
          )}

          {/* ===== MODERN TABLE ===== */}
          {!loading && (
            <div className="table-responsive rounded-4 shadow-sm border overflow-hidden animate-fade-in-up">
              <table className="table table-hover align-middle mb-0 border-0">
                <thead className="bg-light-soft text-muted small fw-bold">
                  <tr>
                    <th className="ps-4 py-3">#</th>
                    <th>Node ID</th>
                    <th>Strategic Title</th>
                    <th>Match Reference</th>
                    <th>League</th>
                    <th>Status</th>
                    <th className="text-end">Gross Collection</th>
                    <th className="text-center pe-4">Participation</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => {
                      const gameInfo = item.game_info || {};
                      const financialSummary = item.financial_summary || {};
                      const userCount = item.users_full_summary?.length || 0;

                      return (
                        <tr key={gameInfo.game_id || index}>
                          <td className="ps-4">
                            <span className="text-muted small">{(page - 1) * pageSize + index + 1}</span>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark fw-bold px-3 py-2 border rounded-3">
                              #{gameInfo.game_id || "N/A"}
                            </span>
                          </td>
                          <td>
                            <div className="fw-bold text-dark">{gameInfo.title || "Untitled Session"}</div>
                          </td>
                          <td>
                            <div className="text-dark small fw-medium">{getMatchName(gameInfo.match_id)}</div>
                          </td>
                          <td>
                            <div className="text-muted small fw-medium">{getLeagueName(gameInfo.league_id)}</div>
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(gameInfo.status)} px-3 py-2 fw-medium shadow-sm`}>
                              {(gameInfo.status || "unknown").toUpperCase()}
                            </span>
                          </td>
                          <td className="text-end">
                            <span className="fw-bold text-primary font-monospace">
                              {formatCurrency(financialSummary.total_collection || 0)}
                            </span>
                          </td>
                          <td className="text-center pe-4">
                            <button
                              className="btn btn-primary-soft btn-sm px-3 rounded-pill"
                              onClick={() => handleViewUsers(gameInfo.title, item.users_full_summary || [])}
                              disabled={userCount === 0}>
                              <FiUsers className="me-2"/> {userCount > 0 ? `${userCount} Nodes` : 'No Entry'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <div className="p-5">
                          <FiInbox size={60} className="text-muted opacity-25 mb-4" />
                          <h5 className="fw-bold text-dark mb-2">Zero Records Found</h5>
                          <p className="text-muted mb-0">No strategic nodes identified in this sector</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ===== MODERN PAGINATION ===== */}
          {totalPages > 1 && !loading && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
              <div className="text-muted small fw-medium">
                Revealing <span className="text-dark fw-bold">{(page - 1) * pageSize + 1}</span> to{" "}
                <span className="text-dark fw-bold">{Math.min(page * pageSize, filteredData.length)}</span> of{" "}
                <span className="text-primary fw-bold font-monospace">{filteredData.length}</span> strategic points
              </div>
              
              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0 gap-1">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link border-0 rounded-3 px-3 shadow-sm"
                      onClick={() => setPage(page - 1)}
                    >
                      &laquo;
                    </button>
                  </li>

                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;

                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button className="page-link border-0 rounded-3 px-3 shadow-sm" onClick={() => setPage(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button className="page-link border-0 rounded-3 px-3 shadow-sm" onClick={() => setPage(page + 1)}>
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* ===== PREMIUM USERS MODAL ===== */}
      {isModalOpen && viewUsers && (
        <div className="modal fade show d-block animate-fade-in" style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: 'blur(8px)' }} onClick={closeModal}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-premium rounded-4 overflow-hidden">
              <div className="modal-header py-4 px-4 border-bottom-soft bg-white">
                <div className="d-flex align-items-center gap-3">
                   <div className="bg-primary-soft p-2 rounded-3">
                      <FiUsers className="text-primary" size={20}/>
                   </div>
                   <h5 className="modal-title fw-bold text-dark mb-0">Node Participant Summary</h5>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={closeModal}></button>
              </div>
              
              <div className="modal-body p-0 bg-light-soft">
                {viewUsers.users.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light text-muted extra-small fw-bold border-0 sticky-top">
                        <tr>
                          <th className="ps-4">#</th>
                          <th>Participant Identity</th>
                          <th>UID Factor</th>
                          <th>Mobile Endpoint</th>
                          <th className="text-end">Activity</th>
                          <th className="text-end pe-4">Credit Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewUsers.users.map((u, i) => (
                          <tr key={u.id || i}>
                            <td className="ps-4 text-muted small">{i + 1}</td>
                            <td className="fw-bold text-dark">{u.name || "N/A Participant"}</td>
                            <td><span className="badge bg-light text-muted border">#{u.uid || "N/A"}</span></td>
                            <td className="text-muted font-monospace small">{u.mobile || "HIDDEN"}</td>
                            <td className="text-end fw-medium text-dark">{u.total_entries || 0} Sessions</td>
                            <td className="text-end pe-4 text-success fw-bold font-monospace">
                              {formatCurrency(u.total_won || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-5 text-center bg-white">
                    <FiInbox size={50} className="text-muted opacity-25 mb-3" />
                    <p className="text-muted mb-0">No active nodes identified for this session</p>
                  </div>
                )}
              </div>
              
              <div className="modal-footer py-3 px-4 bg-white border-top-soft justify-content-between">
                <div className="text-muted small fw-medium">
                  Identified Active Nodes: <span className="text-primary fw-bold">{viewUsers.users.length}</span>
                </div>
                <button type="button" className="btn btn-light px-4 rounded-pill shadow-sm" onClick={closeModal}>
                  Close Registry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};